import React, {
	Dispatch,
	memo,
	SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { GridContainer, Input, Select, SpinnerOverlay, SubmissionButtons } from '@components/common';

import useQueryValuesForCreate from '@hooks/configurationDataModelManagement/useQueryValuesForCreate';

import { isEmpty } from '@utils/common';

import { EConfigurationDataFieldNames, IConfigurationDataFormValues } from '@interfaces/cmModule/configurationData';
import { TUseFormReturnCustomError } from '@interfaces/errors/useFormErrors';

import { EValidationMode } from '@enums/validationMode';

import { regExpWithoutSpace } from '@constants/regExp';
import {
	configurationDataFormDefaultRequiredFields,
	configurationDataFormLabels,
	mavenirDataModelTypeOptions,
	mavenirDescriptorTypeOptions,
	mavenirDeviceTypeOptions,
	mavenirElementTypeOptions,
	mavenirGroupByOptions,
	mavenirPnfTypeOptions,
} from '../../constants';

import { mainSchema } from './mainSchema';
import { updateRequiredFields } from './utils';

import styles from './ImportConfigurationDataModelForm.module.scss';

interface IImportConfigurationDataModelFormProps {
	onClose: () => void;
	onSubmitHandler: (values: IConfigurationDataFormValues) => void;
	setIsDirty: Dispatch<SetStateAction<boolean>>;
	setIsConfirmModalNeeded: Dispatch<SetStateAction<boolean>>;
}

const ImportConfigurationDataModelForm = ({
	onClose,
	onSubmitHandler,
	setIsDirty,
	setIsConfirmModalNeeded,
}: IImportConfigurationDataModelFormProps) => {
	const [requiredFields, setRequiredFields]
		= useState<Array<keyof IConfigurationDataFormValues>>(configurationDataFormDefaultRequiredFields);

	const schema = useMemo(() => mainSchema({
		requiredFields,
	}), [requiredFields]);

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		control,
		setValue,
		watch,
		reset,
		clearErrors,
	}: TUseFormReturnCustomError<IConfigurationDataFormValues> = useForm<IConfigurationDataFormValues>({
		mode: EValidationMode.all,
		resolver: yupResolver(schema),
		defaultValues: {
			[EConfigurationDataFieldNames.NAME]: null,
			[EConfigurationDataFieldNames.DESCRIPTOR_TYPE]: null,
			[EConfigurationDataFieldNames.DATA_MODEL_TYPE]: null,
			[EConfigurationDataFieldNames.DEVICE_TYPE]: null,
			[EConfigurationDataFieldNames.VDU_ID]: null,
			[EConfigurationDataFieldNames.DATA_MODEL]: '',
			[EConfigurationDataFieldNames.PNF_TYPE]: null,
			[EConfigurationDataFieldNames.BUILD_VERSION]: null,
			[EConfigurationDataFieldNames.ELEMENT_TYPE]: null,
			[EConfigurationDataFieldNames.GROUP_BY]: null,
			[EConfigurationDataFieldNames.PRODUCT_NAME]: null,
			[EConfigurationDataFieldNames.NS_ID]: null,
			[EConfigurationDataFieldNames.NS_VERSION]: null,
			[EConfigurationDataFieldNames.NF_TYPE]: null,
			[EConfigurationDataFieldNames.COMPONENT_TYPE]: null,
			[EConfigurationDataFieldNames.COMPONENT_VERSION]: null,
		},
	});

	useEffect(() => {
		setIsDirty(isDirty);

		if (isDirty) {
			setIsConfirmModalNeeded(true);
		}
	}, [isDirty, setIsConfirmModalNeeded, setIsDirty]);

	const descriptorType = watch(EConfigurationDataFieldNames.DESCRIPTOR_TYPE)?.value;
	const dataModelType = watch(EConfigurationDataFieldNames.DATA_MODEL_TYPE)?.value;
	const deviceType = watch(EConfigurationDataFieldNames.DEVICE_TYPE)?.value;
	const groupBy = watch(EConfigurationDataFieldNames.GROUP_BY)?.value;

	useEffect(() => {
		updateRequiredFields({
			descriptorType,
			deviceType,
			dataModelType,
			groupBy,
			setValue,
			clearErrors,
			setRequiredFields,
		});
	}, [clearErrors, dataModelType, descriptorType, deviceType, groupBy, setValue]);

	const requiredFieldsValues: Record<string, boolean> = useMemo(() => ({
		[EConfigurationDataFieldNames.NAME]: requiredFields.includes(EConfigurationDataFieldNames.NAME),
		[EConfigurationDataFieldNames.DESCRIPTOR_TYPE]:
			requiredFields.includes(EConfigurationDataFieldNames.DESCRIPTOR_TYPE),
		[EConfigurationDataFieldNames.DATA_MODEL_TYPE]:
			requiredFields.includes(EConfigurationDataFieldNames.DATA_MODEL_TYPE),
		[EConfigurationDataFieldNames.DEVICE_TYPE]: requiredFields.includes(EConfigurationDataFieldNames.DEVICE_TYPE),
		[EConfigurationDataFieldNames.VDU_ID]: requiredFields.includes(EConfigurationDataFieldNames.VDU_ID),
		[EConfigurationDataFieldNames.DATA_MODEL]: requiredFields.includes(EConfigurationDataFieldNames.DATA_MODEL),
		[EConfigurationDataFieldNames.PNF_TYPE]: requiredFields.includes(EConfigurationDataFieldNames.PNF_TYPE),
		[EConfigurationDataFieldNames.BUILD_VERSION]:
			requiredFields.includes(EConfigurationDataFieldNames.BUILD_VERSION),
		[EConfigurationDataFieldNames.ELEMENT_TYPE]: requiredFields.includes(EConfigurationDataFieldNames.ELEMENT_TYPE),
		[EConfigurationDataFieldNames.GROUP_BY]: requiredFields.includes(EConfigurationDataFieldNames.GROUP_BY),
		[EConfigurationDataFieldNames.PRODUCT_NAME]: requiredFields.includes(EConfigurationDataFieldNames.PRODUCT_NAME),
		[EConfigurationDataFieldNames.NS_ID]: requiredFields.includes(EConfigurationDataFieldNames.NS_ID),
		[EConfigurationDataFieldNames.NS_VERSION]: requiredFields.includes(EConfigurationDataFieldNames.NS_VERSION),
		[EConfigurationDataFieldNames.NF_TYPE]: requiredFields.includes(EConfigurationDataFieldNames.NF_TYPE),
		[EConfigurationDataFieldNames.COMPONENT_TYPE]:
			requiredFields.includes(EConfigurationDataFieldNames.COMPONENT_TYPE),
		[EConfigurationDataFieldNames.COMPONENT_VERSION]:
			requiredFields.includes(EConfigurationDataFieldNames.COMPONENT_VERSION),
	}), [requiredFields]);

	const onSubmit = useCallback((values: IConfigurationDataFormValues) => {
		onSubmitHandler(values);
		reset({
			[EConfigurationDataFieldNames.NAME]: watch(EConfigurationDataFieldNames.NAME),
			[EConfigurationDataFieldNames.DESCRIPTOR_TYPE]: watch(EConfigurationDataFieldNames.DESCRIPTOR_TYPE),
			[EConfigurationDataFieldNames.DATA_MODEL_TYPE]: watch(EConfigurationDataFieldNames.DATA_MODEL_TYPE),
			[EConfigurationDataFieldNames.DEVICE_TYPE]: watch(EConfigurationDataFieldNames.DEVICE_TYPE),
			[EConfigurationDataFieldNames.VDU_ID]: watch(EConfigurationDataFieldNames.VDU_ID),
			[EConfigurationDataFieldNames.DATA_MODEL]: watch(EConfigurationDataFieldNames.DATA_MODEL),
			[EConfigurationDataFieldNames.PNF_TYPE]: watch(EConfigurationDataFieldNames.PNF_TYPE),
			[EConfigurationDataFieldNames.BUILD_VERSION]: watch(EConfigurationDataFieldNames.BUILD_VERSION),
			[EConfigurationDataFieldNames.ELEMENT_TYPE]: watch(EConfigurationDataFieldNames.ELEMENT_TYPE),
			[EConfigurationDataFieldNames.GROUP_BY]: watch(EConfigurationDataFieldNames.GROUP_BY),
			[EConfigurationDataFieldNames.PRODUCT_NAME]: watch(EConfigurationDataFieldNames.PRODUCT_NAME),
			[EConfigurationDataFieldNames.NS_ID]: watch(EConfigurationDataFieldNames.NS_ID),
			[EConfigurationDataFieldNames.NS_VERSION]: watch(EConfigurationDataFieldNames.NS_VERSION),
			[EConfigurationDataFieldNames.NF_TYPE]: watch(EConfigurationDataFieldNames.NF_TYPE),
			[EConfigurationDataFieldNames.COMPONENT_TYPE]: watch(EConfigurationDataFieldNames.COMPONENT_TYPE),
			[EConfigurationDataFieldNames.COMPONENT_VERSION]: watch(EConfigurationDataFieldNames.COMPONENT_VERSION),
		});
	}, [onSubmitHandler, reset, watch]);

	const validateNewOption = useCallback((value: string) => {
		return !value.match(regExpWithoutSpace);
	}, []);

	const { valuesForCreate, isLoadingValuesForCreate } = useQueryValuesForCreate();

	return (
		<div>
			{isLoadingValuesForCreate && <SpinnerOverlay />}
			<GridContainer
				columns={2}
				customClassName={styles.importConfigurationDataModelFormContainer}
				gap='15px 20px'
				gridAutoFlow='row'
			>
				<div className={styles.name}>
					<Select
						control={control}
						errorMessage={errors[EConfigurationDataFieldNames.NAME]?.message
							|| errors[EConfigurationDataFieldNames.NAME]?.value?.message}
						isCreatable
						isError={!!errors[EConfigurationDataFieldNames.NAME]
							|| !!errors[EConfigurationDataFieldNames.NAME]?.value}
						label={configurationDataFormLabels[EConfigurationDataFieldNames.NAME]}
						name={EConfigurationDataFieldNames.NAME}
						options={valuesForCreate.names}
						placeholder='Enter name'
						required={requiredFieldsValues[EConfigurationDataFieldNames.NAME]}
						validateNewOption={validateNewOption}
					/>
				</div>
				<div className={styles.descriptionType}>
					<Select
						control={control}
						errorMessage={errors[EConfigurationDataFieldNames.DESCRIPTOR_TYPE]?.message}
						isError={!!errors[EConfigurationDataFieldNames.DESCRIPTOR_TYPE]}
						label={configurationDataFormLabels[EConfigurationDataFieldNames.DESCRIPTOR_TYPE]}
						name={EConfigurationDataFieldNames.DESCRIPTOR_TYPE}
						options={mavenirDescriptorTypeOptions}
						placeholder='Select description type'
						required={requiredFieldsValues[EConfigurationDataFieldNames.DESCRIPTOR_TYPE]}
					/>
				</div>
				<div className={styles.dataModelType}>
					<Select
						control={control}
						errorMessage={errors[EConfigurationDataFieldNames.DATA_MODEL_TYPE]?.message}
						isError={!!errors[EConfigurationDataFieldNames.DATA_MODEL_TYPE]}
						label={configurationDataFormLabels[EConfigurationDataFieldNames.DATA_MODEL_TYPE]}
						name={EConfigurationDataFieldNames.DATA_MODEL_TYPE}
						options={mavenirDataModelTypeOptions}
						placeholder='Select data model type'
						required={requiredFieldsValues[EConfigurationDataFieldNames.DATA_MODEL_TYPE]}
					/>
				</div>
				<div className={styles.deviceType}>
					<Select
						control={control}
						errorMessage={errors[EConfigurationDataFieldNames.DEVICE_TYPE]?.message}
						isError={!!errors[EConfigurationDataFieldNames.DEVICE_TYPE]}
						label={configurationDataFormLabels[EConfigurationDataFieldNames.DEVICE_TYPE]}
						name={EConfigurationDataFieldNames.DEVICE_TYPE}
						options={mavenirDeviceTypeOptions}
						placeholder='Select device type'
						required={requiredFieldsValues[EConfigurationDataFieldNames.DEVICE_TYPE]}
					/>
				</div>
				{requiredFieldsValues[EConfigurationDataFieldNames.VDU_ID]
					&& <div className={styles.vduId}>
						<Select
							control={control}
							errorMessage={errors[EConfigurationDataFieldNames.VDU_ID]?.message
								|| errors[EConfigurationDataFieldNames.VDU_ID]?.value?.message}
							isCreatable
							isError={!!errors[EConfigurationDataFieldNames.VDU_ID]
								|| errors[EConfigurationDataFieldNames.VDU_ID]?.value}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.VDU_ID]}
							name={EConfigurationDataFieldNames.VDU_ID}
							options={valuesForCreate.vduIds}
							placeholder='Enter vdu id'
							required
						/>
					</div>
				}
				{requiredFieldsValues[EConfigurationDataFieldNames.DATA_MODEL]
					&& <div className={styles.dataModel}>
						<Input
							errorMessage={errors[EConfigurationDataFieldNames.DATA_MODEL]?.message}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.DATA_MODEL]}
							name={EConfigurationDataFieldNames.DATA_MODEL}
							placeholder='Enter data model'
							register={register}
							required
						/>
					</div>
				}
				{requiredFieldsValues[EConfigurationDataFieldNames.PNF_TYPE]
					&& <div className={styles.pnfType}>
						<Select
							control={control}
							errorMessage={errors[EConfigurationDataFieldNames.PNF_TYPE]?.message}
							isError={!!errors[EConfigurationDataFieldNames.PNF_TYPE]}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.PNF_TYPE]}
							name={EConfigurationDataFieldNames.PNF_TYPE}
							options={mavenirPnfTypeOptions}
							placeholder='Select pnf type'
							required
						/>
					</div>
				}
				{requiredFieldsValues[EConfigurationDataFieldNames.BUILD_VERSION]
					&& <div className={styles.buildVersion}>
						<Select
							control={control}
							errorMessage={errors[EConfigurationDataFieldNames.BUILD_VERSION]?.message
								|| (errors[EConfigurationDataFieldNames.BUILD_VERSION])?.value?.message}
							isCreatable
							isError={!!errors[EConfigurationDataFieldNames.BUILD_VERSION]
								|| errors[EConfigurationDataFieldNames.BUILD_VERSION]?.value}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.BUILD_VERSION]}
							name={EConfigurationDataFieldNames.BUILD_VERSION}
							options={valuesForCreate.buildVersions}
							placeholder='Enter build version'
							required
						/>
					</div>
				}
				{requiredFieldsValues[EConfigurationDataFieldNames.ELEMENT_TYPE]
					&& <div className={styles.elementType}>
						<Select
							control={control}
							errorMessage={errors[EConfigurationDataFieldNames.ELEMENT_TYPE]?.message}
							isError={!!errors[EConfigurationDataFieldNames.ELEMENT_TYPE]}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.ELEMENT_TYPE]}
							name={EConfigurationDataFieldNames.ELEMENT_TYPE}
							options={mavenirElementTypeOptions}
							placeholder='Select element type'
							required
						/>
					</div>
				}
				{requiredFieldsValues[EConfigurationDataFieldNames.GROUP_BY]
					&& <div className={styles.groupBy}>
						<Select
							control={control}
							errorMessage={errors[EConfigurationDataFieldNames.GROUP_BY]?.message}
							isError={!!errors[EConfigurationDataFieldNames.GROUP_BY]}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.GROUP_BY]}
							name={EConfigurationDataFieldNames.GROUP_BY}
							options={mavenirGroupByOptions}
							placeholder='Select group by'
							required
						/>
					</div>
				}
				{requiredFieldsValues[EConfigurationDataFieldNames.PRODUCT_NAME]
					&& <div className={styles.productName}>
						<Select
							control={control}
							errorMessage={errors[EConfigurationDataFieldNames.PRODUCT_NAME]?.message
								|| errors[EConfigurationDataFieldNames.PRODUCT_NAME]?.value?.message}
							isCreatable
							isError={!!errors[EConfigurationDataFieldNames.PRODUCT_NAME]
								|| errors[EConfigurationDataFieldNames.PRODUCT_NAME]?.value}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.PRODUCT_NAME]}
							name={EConfigurationDataFieldNames.PRODUCT_NAME}
							options={valuesForCreate.productNames}
							placeholder='Enter product name'
							required
						/>
					</div>
				}
				{requiredFieldsValues[EConfigurationDataFieldNames.NS_ID]
					&& <div className={styles.nsId}>
						<Select
							control={control}
							errorMessage={errors[EConfigurationDataFieldNames.NS_ID]?.message
								|| errors[EConfigurationDataFieldNames.NS_ID]?.value?.message}
							isCreatable
							isError={!!errors[EConfigurationDataFieldNames.NS_ID]
								|| errors[EConfigurationDataFieldNames.NS_ID]?.value}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.NS_ID]}
							name={EConfigurationDataFieldNames.NS_ID}
							options={valuesForCreate.nsIds}
							placeholder='Enter ns id'
							required
						/>
					</div>
				}
				{requiredFieldsValues[EConfigurationDataFieldNames.NS_VERSION]
					&& <div className={styles.nsVersion}>
						<Select
							control={control}
							errorMessage={errors[EConfigurationDataFieldNames.NS_VERSION]?.message
								|| errors[EConfigurationDataFieldNames.NS_VERSION]?.value?.message}
							isCreatable
							isError={!!errors[EConfigurationDataFieldNames.NS_VERSION]
								|| errors[EConfigurationDataFieldNames.NS_VERSION]?.value}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.NS_VERSION]}
							name={EConfigurationDataFieldNames.NS_VERSION}
							options={valuesForCreate.nsVersions}
							placeholder='Enter ns version'
							required
						/>
					</div>
				}
				{requiredFieldsValues[EConfigurationDataFieldNames.NF_TYPE]
					&& <div className={styles.nfType}>
						<Select
							control={control}
							errorMessage={errors[EConfigurationDataFieldNames.NF_TYPE]?.message
								|| errors[EConfigurationDataFieldNames.NF_TYPE]?.value?.message}
							isCreatable
							isError={!!errors[EConfigurationDataFieldNames.NF_TYPE]
								|| errors[EConfigurationDataFieldNames.NF_TYPE]?.value}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.NF_TYPE]}
							name={EConfigurationDataFieldNames.NF_TYPE}
							options={valuesForCreate.nfTypes}
							placeholder='Enter nf type'
							required
						/>
					</div>
				}
				{requiredFieldsValues[EConfigurationDataFieldNames.COMPONENT_TYPE]
					&& <div className={styles.componentType}>
						<Select
							control={control}
							errorMessage={errors[EConfigurationDataFieldNames.COMPONENT_TYPE]?.message
								|| errors[EConfigurationDataFieldNames.COMPONENT_TYPE]?.value?.message}
							isCreatable
							isError={!!errors[EConfigurationDataFieldNames.COMPONENT_TYPE]
								|| errors[EConfigurationDataFieldNames.COMPONENT_TYPE]?.value}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.COMPONENT_TYPE]}
							name={EConfigurationDataFieldNames.COMPONENT_TYPE}
							options={valuesForCreate.componentTypes}
							placeholder='Enter component type'
							required
						/>
					</div>
				}
				{requiredFieldsValues[EConfigurationDataFieldNames.COMPONENT_VERSION]
					&& <div className={styles.componentVersion}>
						<Select
							control={control}
							errorMessage={errors[EConfigurationDataFieldNames.COMPONENT_VERSION]?.message
								|| errors[EConfigurationDataFieldNames.COMPONENT_VERSION]?.value?.message}
							isCreatable
							isError={!!errors[EConfigurationDataFieldNames.COMPONENT_VERSION]
								|| errors[EConfigurationDataFieldNames.COMPONENT_VERSION]?.value}
							label={configurationDataFormLabels[EConfigurationDataFieldNames.COMPONENT_VERSION]}
							name={EConfigurationDataFieldNames.COMPONENT_VERSION}
							options={valuesForCreate.componentVersions}
							placeholder='Enter component version'
							required
						/>
					</div>
				}
			</GridContainer>
			<div className={styles.submitButtons}>
				<SubmissionButtons
					disabledPositive={!isEmpty(errors)}
					negativeHandler={onClose}
					positiveHandler={handleSubmit(onSubmit)}
					positiveLabel='Next'
				/>
			</div>
		</div>
	);
};

export default memo(ImportConfigurationDataModelForm);
